import { CaretRight } from '@phosphor-icons/react';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Badge } from '@reelplicate/ui';
import { Link as RouterLink } from 'react-router';

import { SETTINGS_SECTIONS } from './sections';

/** A warning shown on a row, e.g. accounts that need reconnecting (none until OAuth). */
const SECTION_STATUS: Record<string, string> = {};

/**
 * Figma "Settings — Index" (325:3457): page header, then one card listing
 * every settings area - a 40px icon tile, title and one-line description,
 * an optional warning badge, and a caret. Each row opens its page.
 */
export function SettingsPage() {
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0]?.name;

  return (
    <div className="flex flex-col items-center gap-6 px-6 pt-10 pb-16 lg:px-14">
      <header className="flex w-full max-w-220 flex-col gap-1">
        <h1 className="text-heading-h1 text-text-primary">Settings</h1>
        <p className="text-body-md text-text-secondary">
          Manage {workspace ? `the ${workspace} workspace` : 'your workspace'} and your
          own account.
        </p>
      </header>

      <nav
        aria-label="Settings"
        className="bg-bg-surface border-border-subtle w-full max-w-220 overflow-hidden rounded-xl border px-6"
      >
        <ul>
          {SETTINGS_SECTIONS.map(({ path, title, description, icon: Icon }) => {
            const status = SECTION_STATUS[path];
            return (
              <li key={path} className="border-border-subtle border-b last:border-b-0">
                <RouterLink
                  to={path}
                  className="group/row focus-visible:ring-border-focus -mx-6 flex items-center gap-4 px-6 py-5 outline-none focus-visible:ring-2 focus-visible:ring-inset"
                >
                  <span className="bg-bg-subtle flex size-10 shrink-0 items-center justify-center rounded-md">
                    <Icon aria-hidden className="text-icon-primary size-5" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-body-lg-strong text-text-primary">{title}</span>
                    <span className="text-body-sm text-text-secondary">
                      {description}
                    </span>
                  </span>
                  {status && (
                    <Badge tone="warning" dot className="shrink-0">
                      {status}
                    </Badge>
                  )}
                  <CaretRight
                    aria-hidden
                    className="text-icon-secondary group-hover/row:text-icon-primary size-5 shrink-0 transition-transform duration-[120ms] ease-out group-hover/row:translate-x-0.5"
                  />
                </RouterLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
